import React, { useEffect, useState } from "react";
import axios from "axios";
const formatSessionTime = (value) => {
  if (!value) return "Unknown";
  try {
    return new Date(value).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return "Unknown";
  }
};

const REVOKE_ALL = "__all__";

/**
 * Shown when login returns MAX_SESSIONS_REACHED (2 devices already active).
 * Signing out a device also signs this device in, in the same request, so the
 * user never has to re-enter credentials or repeat the Google flow.
 */
const ActiveLoginSessionsGate = ({
  darkMode,
  sessions,
  sessionManagementToken,
  email,
  password,
  onSignedIn,
  onResolved,
  onCancel,
}) => {
  const [currentSessions, setCurrentSessions] = useState(sessions || []);
  const [managementToken, setManagementToken] = useState(
    sessionManagementToken || "",
  );
  const [busySessionId, setBusySessionId] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    setCurrentSessions(sessions || []);
  }, [sessions]);

  useEffect(() => {
    setManagementToken(sessionManagementToken || "");
  }, [sessionManagementToken]);

  const postRevoke = (target, useCredentials) => {
    const body = {
      signIn: true,
      ...(target === REVOKE_ALL ? { revokeAll: true } : { sessionId: target }),
      ...(useCredentials || !managementToken
        ? { email, password }
        : { sessionManagementToken: managementToken }),
    };
    return axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/auth/revoke-login-session`,
      body,
      { withCredentials: true },
    );
  };

  const revoke = async (target) => {
    setError("");
    setBusySessionId(target);
    const hasCredentials = Boolean(email && password);
    try {
      let response;
      try {
        response = await postRevoke(target, false);
      } catch (err) {
        if (err.response?.status === 401 && managementToken && hasCredentials) {
          response = await postRevoke(target, true);
        } else {
          throw err;
        }
      }

      const data = response.data || {};
      if (data.accessToken && onSignedIn) {
        onSignedIn(data.accessToken);
      } else if (data.success) {
        onResolved?.(data);
      } else {
        setError(data.message || "Could not log out that device.");
      }
    } catch (err) {
      const data = err.response?.data;
      if (data?.error === "MAX_SESSIONS_REACHED") {
        setCurrentSessions(data.activeLoginSessions || []);
        setManagementToken(data.sessionManagementToken || "");
        setError(
          "Another device signed in at the same time. Please choose a device to log out again.",
        );
      } else {
        setError(
          data?.message || "Could not log out that device. Please try again.",
        );
      }
    } finally {
      setBusySessionId(null);
    }
  };

  const busy = !!busySessionId;
  const mutedText = darkMode ? "text-gray-400" : "text-gray-600";

  return (
    <div className="flex flex-col gap-4 mt-2 md:mt-4">
      <div>
        <h2 className="text-2xl font-semibold">Device limit reached</h2>
        <p className={`text-sm mt-2 ${mutedText}`}>
          You&apos;re already logged in on 2 devices. Log out of one to sign in
          here.
        </p>
      </div>

      {currentSessions.length > 0 ? (
        <ul className="flex flex-col gap-3">
          {currentSessions.map((session) => (
            <li
              key={session.sessionId}
              className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 border p-3 rounded-lg ${
                darkMode ? "border-[#333] bg-[#1A1A1A]" : "border-gray-200 bg-gray-50"
              }`}
            >
              <div className="min-w-0">
                <div className="font-medium truncate">
                  {session.deviceLabel || "Unknown device"}
                </div>
                <div className={`text-xs mt-1 ${mutedText}`}>
                  Last active: {formatSessionTime(session.lastActiveAt)}
                  {session.ip ? ` · ${session.ip}` : ""}
                </div>
              </div>
              <button
                type="button"
                className="greenButton !mt-0 !mx-0 shrink-0 text-sm"
                disabled={busy}
                onClick={() => revoke(session.sessionId)}
              >
                {busySessionId === session.sessionId
                  ? "Logging out…"
                  : "Log out & continue"}
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className={`text-sm ${mutedText}`}>
          We couldn&apos;t load the list of your other devices, but you can
          still log them out and continue here.
        </p>
      )}

      <button
        type="button"
        className="greenButton !mt-0 mx-auto text-sm"
        disabled={busy}
        onClick={() => revoke(REVOKE_ALL)}
      >
        {busySessionId === REVOKE_ALL
          ? "Logging out other devices…"
          : "Log out all other devices & continue"}
      </button>

      {error && (
        <p className="text-[var(--primary)] text-sm font-medium">{error}</p>
      )}

      <button
        type="button"
        onClick={onCancel}
        disabled={busy}
        className={`text-sm mx-auto hover:underline ${
          darkMode
            ? "text-gray-400 hover:text-gray-300"
            : "text-gray-600 hover:text-gray-800"
        }`}
      >
        ← Back to Sign In
      </button>
    </div>
  );
};

export default ActiveLoginSessionsGate;

export const isMaxSessionsError = (error) =>
  error?.response?.data?.error === "MAX_SESSIONS_REACHED";
